/* eslint-disable no-duplicate-imports */
import { clone } from '@jack-henry/frontend-utils/functions';
import '@treasury/domain-components/layout/tm-list-layout';
import { Account } from '@treasury/domain/accounts';
import { TmBaseComponent } from '@treasury/presentation';
import {
    circleCheckmarkFilledIcon,
    circleCloseIcon,
    editIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-button';
import '@treasury/presentation/components/tm-icon';
import { TmSearchTermChangeEvent } from '@treasury/presentation/components/tm-list-header';
import '@treasury/presentation/components/tm-text-field';
import '@treasury/presentation/components/vendor/tm-pagination-controls';
import { TmPaginationControls } from '@treasury/presentation/components/vendor/tm-pagination-controls';
import '@vaadin/grid';
import { type Grid } from '@vaadin/grid';
import { css, html, nothing, PropertyValues } from 'lit';
import { columnBodyRenderer } from 'lit-vaadin-helpers';
import { customElement, property, query, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { filterTableResults } from '../../../utilities/table-result-filter';
import { classMap } from 'lit/directives/class-map.js';

export const tagName = 'account-nicknames-table';
@customElement(tagName)
export class AccountNicknamesTable extends TmBaseComponent {
    @property({ type: Boolean })
    public loading = true;

    @property({ type: Boolean })
    public isDesktop = false;

    @property({ type: Array })
    public accounts: Account[] = [];

    @property({ type: Array })
    public filteredAccounts: Account[] = [];

    @property({ type: Array })
    public editedFilteredAccounts: Account[] = [];

    @property({ type: Boolean })
    public bulkEditMode = false;

    @state()
    public editedSingleAccount?: Account;

    @state()
    private filteredAccountsCount = 0;

    @state()
    private searchTerm = '';

    @query('tm-pagination-controls')
    private paginationControls!: TmPaginationControls;

    private readonly gridRef = createRef<Grid<Account>>();

    protected render() {
        return html`${this.renderSearchBar()}
        ${this.renderTable()}${this.renderPaginationControls()}`;
    }

    protected updated(_changedProperties: PropertyValues): void {
        if (_changedProperties.has('accounts')) {
            this.filteredAccounts = this.accounts;
            this.filteredAccountsCount = this.accounts.length;
        }
        if (
            _changedProperties.has('bulkEditMode') ||
            _changedProperties.has('editedSingleAccount')
        ) {
            this.gridRef.value?.requestContentUpdate();
            if (this.bulkEditMode) {
                this.editedFilteredAccounts = this.accounts.map(
                    acc => new Account({ ...acc.toDto() })
                );
                this.editedSingleAccount = undefined;
            } else {
                this.editedFilteredAccounts = [];
            }
        }
    }

    private get columns() {
        return html`<vaadin-grid-sort-column
                header="Account Number"
                path="number"
                auto-width
                ${columnBodyRenderer(
                    (account: Account) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${account.number}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Account Type"
                path="type"
                ${columnBodyRenderer(
                    (account: Account) =>
                        html`<div class="flex flex-col">
                            <span class="text-sm">${account.type}</span>
                        </div>`,
                    []
                )}
            ></vaadin-grid-sort-column>
            <vaadin-grid-sort-column
                header="Account Nickname"
                path="name"
                ${columnBodyRenderer((account: Account) => {
                    return html`<div class="flex">
                        <div class="grow">${this.renderAccountNickname(account)}</div>
                        <div class="ml-2.5 text-right">${this.renderInlineButtons(account)}</div>
                    </div>`;
                }, [])}
            ></vaadin-grid-sort-column>`;
    }

    private isSingleAccountBeingEdited(account: Account) {
        return this.editedSingleAccount && this.editedSingleAccount.id === account.id;
    }

    private isSingleAccountChanged(account: Account) {
        return this.editedSingleAccount && this.editedSingleAccount.name !== account.name;
    }

    private filterResults(e?: CustomEvent) {
        let filteredAccounts = clone(this.accounts);
        if (e) {
            this.searchTerm = e.detail.value;
        }
        filteredAccounts = filterTableResults(
            {
                detail: { value: this.searchTerm },
            } as CustomEvent,
            this.accounts,
            ['number', 'type', 'name']
        );
        this.filteredAccountsCount = filteredAccounts.length;
        if (this.isDesktop) {
            const offset = this.paginationControls?.calculateOffset();
            const limit = this.paginationControls?.pageSize;
            this.filteredAccounts = filteredAccounts.slice(offset, offset + limit);
        } else {
            this.filteredAccounts = filteredAccounts;
        }
    }

    private updateAccount(account: Account) {
        this.dispatchEvent(
            new CustomEvent<Account>('update-account', {
                detail: account,
            })
        );
    }

    private changedBulkAccounts() {
        this.dispatchEvent(
            new CustomEvent<Account[]>('changed-bulk-accounts', {
                detail: this.editedFilteredAccounts,
            })
        );
    }

    private renderSearchBar() {
        if (this.bulkEditMode) {
            return nothing;
        }
        return html`<tm-list-header
            .searchTerm=${this.searchTerm}
            @search-term-change=${(e: TmSearchTermChangeEvent) => this.filterResults(e)}
        >
        </tm-list-header>`;
    }

    private renderAccountNickname(account: Account) {
        return this.isSingleAccountBeingEdited(account) || this.bulkEditMode
            ? html`<tm-text-field
                  noouterspacing
                  required
                  .initial=${account.name}
                  @value-changed=${(e: CustomEvent) => {
                      if (this.bulkEditMode) {
                          const index = this.editedFilteredAccounts.findIndex(
                              acc => acc.id === account.id
                          );
                          if (index !== -1) {
                              this.editedFilteredAccounts[index].name = e.detail.value;
                          }
                          this.changedBulkAccounts();
                          return;
                      }
                      if (this.editedSingleAccount) {
                          this.editedSingleAccount.name = e.detail.value;
                          this.gridRef.value?.requestContentUpdate();
                      }
                  }}
              ></tm-text-field>`
            : html`<span class="text-sm">${account.name}</span>`;
    }

    private renderInlineButtons(account: Account) {
        if (this.bulkEditMode) {
            return nothing;
        }
        return this.isSingleAccountBeingEdited(account)
            ? html`
                  <tm-icon
                      class="mr-1.5"
                      .icon=${circleCheckmarkFilledIcon}
                      .color=${this.isSingleAccountChanged(account)
                          ? 'var(--brand-primary)'
                          : 'var(--tm-color-gray-medium)'}
                      @click=${() =>
                          this.editedSingleAccount &&
                          this.isSingleAccountChanged(account) &&
                          this.updateAccount(this.editedSingleAccount)}
                      @keyup=${(e: KeyboardEvent) =>
                          e.key === 'Return' &&
                          this.editedSingleAccount &&
                          this.isSingleAccountChanged(account) &&
                          this.updateAccount(this.editedSingleAccount)}
                  >
                  </tm-icon>
                  <tm-icon
                      .icon=${circleCloseIcon}
                      color="var(--tm-color-gray-medium)"
                      @click=${() => {
                          this.editedSingleAccount = undefined;
                      }}
                      @keyup=${(e: KeyboardEvent) => {
                          if (e.key === 'Return') {
                              this.editedSingleAccount = undefined;
                          }
                      }}
                  >
                  </tm-icon>
              `
            : html`
                  <tm-icon
                      .icon=${editIcon}
                      @click=${() => {
                          this.editedSingleAccount = new Account({ ...account.toDto() });
                      }}
                  >
                  </tm-icon>
              `;
    }

    private renderTable() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        if (!this.accounts.length) {
            return html`<div class="w-full px-4 py-8 text-center text-sm text-secondary">
                No accounts found
            </div>`;
        }
        return html` <vaadin-grid
            ${ref(this.gridRef)}
            class=${classMap({
                'h-full shadow-md': !this.isDesktop,
                isDesktop: this.isDesktop,
            })}
            .items=${this.filteredAccounts}
        >
            ${this.columns}
        </vaadin-grid>`;
    }

    private renderPaginationControls() {
        if (!this.isDesktop) {
            return nothing;
        }
        return html`<tm-pagination-controls
            .totalItemCount=${this.filteredAccountsCount}
            @page-changed=${() => {
                this.filterResults();
            }}
        ></tm-pagination-controls>`;
    }

    public static get styles() {
        return [
            css`
                vaadin-grid::part(body-cell) {
                    height: 100%;
                    padding: 8px 0;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: AccountNicknamesTable;
    }
}
